import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "sendBtn"]
  
  connect() {
    this.messageContainer = document.getElementById("messages")
    this.queryInput = this.inputTarget
    this.sendBtn = this.sendBtnTarget
    
    // Handle example links
    document.querySelectorAll(".example-link").forEach(link => {
      link.addEventListener("click", (e) => this.handleExample(e))
    })
  }
  
  send(e) {
    e.preventDefault()
    
    const query = this.queryInput.value.trim()
    if (!query) return
    
    // Disable input while processing
    this.queryInput.disabled = true
    this.sendBtn.disabled = true
    
    // Clear input
    this.queryInput.value = ""
    
    // Add user message to chat
    this.addUserMessage(query)
    
    // Show typing indicator
    this.showTypingIndicator()
    
    // Send request
    this.fetchAnswer(query)
  }
  
  handleExample(e) {
    e.preventDefault()
    const question = e.target.dataset.question
    this.queryInput.value = question
    this.queryInput.focus()
  }
  
  addUserMessage(text) {
    const messageDiv = document.createElement("div")
    messageDiv.className = "message user"
    messageDiv.innerHTML = `
      <div>
        <div class="message-bubble">${this.escapeHtml(text)}</div>
        <div class="message-meta">${this.formatTime(new Date())}</div>
      </div>
    `
    this.messageContainer.appendChild(messageDiv)
    this.scrollToBottom()
  }
  
  addBotMessage(response) {
    const messageDiv = document.createElement("div")
    messageDiv.className = "message bot"
    
    let html = `<div>`
    
    // Main answer only
    html += `<div class="message-bubble">`
    html += this.escapeHtml(response.answer)
    html += `</div>`
    
    // Show processing trail if available
    if (response.steps && response.steps.length > 0) {
      html += `<div style="margin-top:6px;font-size:11px;color:#aaa">${response.steps.map(s => this.escapeHtml(s)).join(" → ")}</div>`
    }
    
    // Show source citation for any answer with sources
    if (response.sources && response.sources.length > 0) {
      const src = response.sources[0]
      html += `<div class="sources-link" style="margin-top:6px;font-size:12px;color:#888">`
      html += `Bron: ${this.escapeHtml(src.title)}`
      if (src.similarity) {
        html += ` (${Math.min(src.similarity * 100, 100).toFixed(0)}%)`
      }
      html += `</div>`
    }
    
    // Show full source toggle for curated/chunk answers
    if (response.full_source) {
      html += `<div class="sources-link" style="margin-top:4px">`
      html += `<a href="#" onclick="this.nextElementSibling.style.display=this.nextElementSibling.style.display==='none'?'block':'none';return false">📋 Volledige bron weergeven</a>`
      html += `<div style="display:none;background:#f5f5f5;padding:8px;margin-top:4px;border-radius:4px;font-size:13px;max-height:200px;overflow-y:auto;white-space:pre-wrap">${this.escapeHtml(response.full_source)}</div>`
      html += `</div>`
    }
    
    html += `<div class="message-meta">${this.formatTime(new Date())}</div>`
    html += `</div>`
    
    messageDiv.innerHTML = html
    this.messageContainer.appendChild(messageDiv)
    
    // Source citations shown inline in the answer bubble above
    this.scrollToBottom()
  }
  
  displaySources(sources) {
    const sourcesPanel = document.getElementById("sources-panel")
    const sourcesList = document.getElementById("sources-list")
    if (!sourcesPanel || !sourcesList) return
    
    sourcesList.innerHTML = ""
    sources.forEach((source, index) => {
      const sourceDiv = document.createElement("div")
      sourceDiv.className = "source-item"
      sourceDiv.innerHTML = `
        <div class="source-item-header">
          <strong>${index + 1}. ${this.escapeHtml(source.title)}</strong>
          <span class="source-similarity">${Math.min(source.similarity * 100, 100).toFixed(0)}%</span>
        </div>
        <div class="source-item-meta">
          <span class="source-type">${source.type}</span>
          <span class="source-category">${source.category}</span>
        </div>
      `
      sourcesList.appendChild(sourceDiv)
    })
    
    sourcesPanel.style.display = "block"
  }
  
  showTypingIndicator(label = "Vraag verstuurd") {
    const typingDiv = document.createElement("div")
    typingDiv.className = "message bot"
    typingDiv.id = "typing-indicator"
    typingDiv.innerHTML = `
      <div class="typing-indicator">
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
        <span style="font-size:11px;color:#999;margin-left:8px">${this.escapeHtml(label)}...</span>
      </div>
    `
    this.messageContainer.appendChild(typingDiv)
    this.scrollToBottom()
  }
  
  removeTypingIndicator() {
    const typing = document.getElementById("typing-indicator")
    if (typing) typing.remove()
  }
  
  fetchAnswer(query) {
    const url = "/chat"
    
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ query: query, stream: false })
    })
      .then(response => response.json())
      .then(data => {
        this.removeTypingIndicator()
        
        if (data.status === "success" || data.status === "fallback") {
          this.addBotMessage(data)
        } else {
          this.addBotMessage({
            answer: "Er is een fout opgetreden. Probeer het later opnieuw.",
            sources: [],
            steps: ["Fout opgetreden"]
          })
        }
      })
      .catch(error => {
        console.error("Error:", error)
        this.removeTypingIndicator()
        this.addBotMessage({
          answer: "Verbindingsfout. Controleer uw internetverbinding.",
          sources: [],
          steps: ["Verbindingsfout"]
        })
      })
      .finally(() => {
        this.queryInput.disabled = false
        this.sendBtn.disabled = false
        this.queryInput.focus()
      })
  }
  
  scrollToBottom() {
    this.messageContainer.scrollTop = this.messageContainer.scrollHeight
  }
  
  formatTime(date) {
    return date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })
  }
  
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
